import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Sidebar from "../../components/Sidebar";
import { DashboardAPI, MyPrescriptionRow } from "../../services/dashboard.service";
import { DiagnosisAPI } from "../../services/diagnosis.service";

const STATUS_STYLES: Record<string, string> = {
  active:        "bg-[#dcfce7] text-[#15803d]",
  draft:         "bg-[#ffedd5] text-[#ea580c]",
  discontinued:  "bg-[#fee2e2] text-[#dc2626]",
  pending:       "bg-[#e5edff] text-[#1c64f2]",
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
  });
}

const DoctorPrescriptionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows]       = useState<MyPrescriptionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);
  // prescription_id currently being opened (fetching its PDF URL).
  const [busyId, setBusyId]   = useState<string | null>(null);

  useEffect(() => {
    DashboardAPI.getMyPrescriptions()
      .then((r) => setRows(r.prescriptions))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#f5f5f7] flex">
      <Sidebar />
      <div className="flex-1 min-w-0 flex flex-col lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0">
        <header className="h-14 border-b border-[#e2e4ea] bg-white flex items-center px-6 sticky top-0 z-20">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 text-[#6b7280] hover:text-[#111827] transition-colors"
            aria-label="Go back"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-sm font-semibold text-[#111827]">My Prescription History</span>
        </header>

        <div className="p-4 sm:p-6 lg:p-8 space-y-4 max-w-4xl">

          {loading && (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-20 rounded-2xl bg-white border border-[#e2e4ea] animate-pulse" />
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-16 text-sm text-red-500">
              Failed to load prescription history.
            </div>
          )}

          {!loading && !error && rows.length === 0 && (
            <div className="text-center py-16 text-[#6b7280] text-sm">
              No prescriptions found.
            </div>
          )}

          {!loading && !error && rows.length > 0 && (
            <div className="bg-white border border-[#e2e4ea] rounded-2xl overflow-hidden">
              {/* Table header */}
              <div className="bg-[#f5f5f7] px-5 py-3 hidden sm:grid grid-cols-[1fr_140px_100px_80px] gap-4 text-xs font-medium text-[#6b7280]">
                <span>Patient</span>
                <span>Date</span>
                <span>Diagnosis</span>
                <span>Status</span>
              </div>

              <div className="divide-y divide-[#f3f4f6]">
                {rows.map((rx) => {
                  // Rows are ACTIVE (finalized) prescriptions, so each has a PDF on
                  // file. Clicking opens that PDF in a new tab, generating it on the
                  // fly if it was never rendered.
                  const canOpen = !!rx.visit_id;
                  const busy = busyId === rx.prescription_id;
                  const openRx = async () => {
                    const vid = rx.visit_id;
                    if (!vid || busyId) return;
                    // Open the tab inside the click gesture so the browser doesn't
                    // block it, then point it at the presigned URL once it resolves.
                    const win = window.open("about:blank", "_blank");
                    if (win) win.opener = null;
                    setBusyId(rx.prescription_id);
                    try {
                      const res = await DiagnosisAPI.getPdfUrl(vid, rx.prescription_id)
                        .catch(() => DiagnosisAPI.generatePdf(vid, rx.prescription_id));
                      if (win) win.location.href = res.pdf_url;
                      else window.open(res.pdf_url, "_blank", "noopener,noreferrer");
                    } catch {
                      win?.close();
                      toast.error("Couldn't open the prescription PDF.");
                    } finally {
                      setBusyId(null);
                    }
                  };
                  return (
                  <div
                    key={rx.prescription_id}
                    onClick={openRx}
                    role={canOpen ? "button" : undefined}
                    tabIndex={canOpen ? 0 : undefined}
                    aria-busy={busy}
                    onKeyDown={(e) => { if (canOpen && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); openRx(); } }}
                    className={`px-5 py-4 flex flex-col sm:grid sm:grid-cols-[1fr_140px_100px_80px] sm:items-center gap-2 sm:gap-4 transition-colors ${canOpen ? "cursor-pointer hover:bg-[#fafafa]" : ""} ${busy ? "opacity-60 pointer-events-none" : ""}`}
                  >
                    {/* Patient name + medication count */}
                    <div>
                      <p className="text-sm font-semibold text-[#111827]">{rx.patient_name}</p>
                      <p className="text-xs text-[#6b7280] mt-0.5">
                        {rx.medication_count} medication{rx.medication_count !== 1 ? "s" : ""}
                      </p>
                    </div>

                    {/* Date */}
                    <p className="text-sm text-[#111827]">
                      {formatDate(rx.prescribed_at ?? rx.visit_date)}
                    </p>

                    {/* Diagnosis */}
                    <p className="text-xs text-[#6b7280] truncate" title={rx.diagnosis_label ?? ""}>
                      {rx.diagnosis_label || <span className="italic">—</span>}
                    </p>

                    {/* Status badge */}
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium w-fit capitalize ${
                        STATUS_STYLES[rx.status?.toLowerCase()] ?? "bg-[#f5f5f7] text-[#6b7280]"
                      }`}
                    >
                      {rx.status}
                    </span>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DoctorPrescriptionsPage;
